import { createInsertSchema } from "drizzle-zod";
import { type ExpressionBuilder, type QueryRowType, type Transaction } from "@rocicorp/zero";
import { accessDenied } from "@corpuscomplete/core/utils";
import type { UserActor } from "@corpuscomplete/core/actor";
import { z } from "zod/v4";

import { AccessGrant } from "../grant";
import { Group } from "../group";
import { group, groupMember } from "../group/group.sql";
import { Scope } from "../scope";
import { MealSchedule } from "../../nutrition/meal-schedule";
import { zql, type Schema } from "../../zero/schema";
import { defineQuery } from "../../zero/transformer";
import { withZeroTransaction } from "../../zero/utils";
import { acquireAdvisoryLockIfServer } from "../../drizzle/advisory-lock";

export namespace Space {
  export const Permissions = AccessGrant.definePermissions({
    canRename: AccessGrant.criteria("space", "root", ["owner", "editor"]),
    canDelete: AccessGrant.criteria("space", "root", ["owner"]),
    canInvite: AccessGrant.criteria("space", "contents", ["editor"]),
    canRemoveMembers: AccessGrant.criteria("space", "contents", ["editor"]),
    canRevokeInvite: AccessGrant.criteria("space", "contents", ["editor"]),
    canSetRootGrant: AccessGrant.criteria("space", "root", ["owner", "editor"]),
    canSetMembersGrant: AccessGrant.criteria("space", "root", ["owner", "editor"]),
    canViewMemberGrants: AccessGrant.criteria("space", "root", ["owner", "editor", "viewer"]),
  });

  export type RootRole = "owner" | "editor" | "viewer" | null;
  export type ContentsRole = "editor" | "viewer" | null;

  export interface AllowedRoleChanges {
    rootRoles: RootRole[];
    contentsRoles: ContentsRole[];
    canModify: boolean;
  }

  export interface SelfRoleChanges {
    rootRoles: RootRole[];
    contentsRoles: ContentsRole[];
    canModify: boolean;
  }

  type GrantLike = { resourceType: string; resourceScope: string; role: string };

  export function getGrant<T extends GrantLike>(
    grants: readonly T[],
    scope: "root" | "contents",
  ): T | undefined {
    return grants.find((g) => g.resourceType === "space" && g.resourceScope === scope);
  }

  export function computeAllowedRootRoleChanges(
    actorRootRole: RootRole,
    targetRootRole: RootRole,
  ): { allowedRoles: RootRole[]; canModify: boolean } {
    const actorIsOwner = actorRootRole === "owner";
    const actorIsEditor = actorRootRole === "editor";

    const targetIsOwner = targetRootRole === "owner";
    const targetIsEditor = targetRootRole === "editor";

    if (!actorIsOwner && !actorIsEditor) {
      return { allowedRoles: [], canModify: false };
    }

    if (!actorIsOwner && (targetIsOwner || targetIsEditor)) {
      return { allowedRoles: [], canModify: false };
    }

    if (actorIsOwner) {
      if (targetIsOwner) {
        return { allowedRoles: [], canModify: false };
      }
      return { allowedRoles: ["owner", "editor", "viewer", null], canModify: true };
    }

    return { allowedRoles: ["viewer", null], canModify: true };
  }

  export function computeAllowedContentsRoleChanges(
    actorRootRole: RootRole,
    targetContentsRole: ContentsRole,
  ): { allowedRoles: ContentsRole[]; canModify: boolean } {
    const actorIsOwner = actorRootRole === "owner";
    const actorIsEditor = actorRootRole === "editor";

    if (!actorIsOwner && !actorIsEditor) {
      return { allowedRoles: [], canModify: false };
    }

    if (actorIsOwner) {
      return { allowedRoles: ["editor", "viewer", null], canModify: true };
    }

    if (targetContentsRole === "editor") {
      return { allowedRoles: [], canModify: false };
    }

    return { allowedRoles: ["viewer", null], canModify: true };
  }

  export function computeAllowedRoleChanges(
    actorRootRole: RootRole,
    targetRootRole: RootRole,
  ): AllowedRoleChanges {
    const rootChanges = computeAllowedRootRoleChanges(actorRootRole, targetRootRole);
    if (!rootChanges.canModify) {
      return { rootRoles: [], contentsRoles: [], canModify: false };
    }
    return {
      rootRoles: rootChanges.allowedRoles,
      contentsRoles: actorRootRole === "owner" ? ["editor", "viewer", null] : ["viewer", null],
      canModify: true,
    };
  }

  export function getAllowedRoleChanges<T extends GrantLike>(
    actorGrants: readonly T[],
    targetGrants: readonly T[],
  ): AllowedRoleChanges {
    const actorRootGrant = getGrant(actorGrants, "root");
    const targetRootGrant = getGrant(targetGrants, "root");
    const actorRootRole = (actorRootGrant?.role as RootRole) ?? null;
    const targetRootRole = (targetRootGrant?.role as RootRole) ?? null;
    return computeAllowedRoleChanges(actorRootRole, targetRootRole);
  }

  export function buildGrantsByUserId<T extends { granteeId: string }>(
    grants: readonly T[],
  ): Map<string, T[]> {
    const map = new Map<string, T[]>();
    for (const grant of grants) {
      const existing = map.get(grant.granteeId);
      if (existing) {
        existing.push(grant);
      } else {
        map.set(grant.granteeId, [grant]);
      }
    }
    return map;
  }

  export function computeSelfRoleChanges(
    actorRootRole: RootRole,
    actorContentsRole: ContentsRole,
    ownerCount: number,
  ): SelfRoleChanges {
    const actorIsOwner = actorRootRole === "owner";
    const isLastOwner = actorIsOwner && ownerCount === 1;

    if (isLastOwner) {
      return { rootRoles: [], contentsRoles: [], canModify: false };
    }

    if (actorIsOwner) {
      return {
        rootRoles: ["editor", "viewer", null],
        contentsRoles: ["editor", "viewer", null],
        canModify: true,
      };
    }

    const hasAnyGrant = actorRootRole !== null || actorContentsRole !== null;
    if (!hasAnyGrant) {
      return { rootRoles: [], contentsRoles: [], canModify: false };
    }

    const rootRoles: RootRole[] = [];
    const contentsRoles: ContentsRole[] = [];

    if (actorRootRole === "editor") {
      rootRoles.push("viewer", null);
    } else if (actorRootRole === "viewer") {
      rootRoles.push(null);
    }

    if (actorContentsRole === "editor") {
      contentsRoles.push("viewer", null);
    } else if (actorContentsRole === "viewer") {
      contentsRoles.push(null);
    }

    return {
      rootRoles,
      contentsRoles,
      canModify: rootRoles.length > 0 || contentsRoles.length > 0,
    };
  }

  export function getSelfRoleChanges<T extends GrantLike>(
    actorGrants: readonly T[],
    ownerCount: number,
  ): SelfRoleChanges {
    const actorRootGrant = getGrant(actorGrants, "root");
    const actorContentsGrant = getGrant(actorGrants, "contents");
    const actorRootRole = (actorRootGrant?.role as RootRole) ?? null;
    const actorContentsRole = (actorContentsGrant?.role as ContentsRole) ?? null;
    return computeSelfRoleChanges(actorRootRole, actorContentsRole, ownerCount);
  }

  export function isPendingInvite(invite: {
    useCount: number | null;
    maxUses: number | null;
  }): boolean {
    const useCount = invite.useCount ?? 0;
    return invite.maxUses === null || useCount < invite.maxUses;
  }

  export async function countOwners(tx: Transaction, groupId: string): Promise<number> {
    const owners = await tx.run(
      zql.accessGrant
        .where("resourceId", "=", groupId)
        .where("resourceType", "=", "space")
        .where("resourceScope", "=", "root")
        .where("role", "=", "owner"),
    );
    return owners.length;
  }

  async function createResources(
    tx: Transaction,
    { id, name }: { id: string; name: string },
  ): Promise<void> {
    const timeNow = Temporal.Now.instant();
    await tx.mutate.space.insert({
      id,
      name,
      timeCreated: timeNow,
      timeUpdated: timeNow,
    });
    await Scope.create(tx, { kind: "space", id });
    await MealSchedule.ensureDefaults(tx, id);
  }

  async function renameResources(
    tx: Transaction,
    { id, name }: { id: string; name: string },
  ): Promise<void> {
    await tx.mutate.space.update({ id, name, timeUpdated: Temporal.Now.instant() });
  }

  async function requireActive(tx: Transaction, id: string) {
    const space = await tx.run(zql.space.where("id", "=", id).one());
    if (!space) {
      throw new Error("Space not found");
    }
    if (space.timeArchived) {
      accessDenied("Cannot manage an archived space");
    }
    return space;
  }

  async function lock(tx: Transaction, id: string): Promise<void> {
    await acquireAdvisoryLockIfServer(tx, "space", id, "exclusive");
  }

  export function viewable(eb: ExpressionBuilder<"space", Schema>, actor: UserActor) {
    const { or, exists } = eb;
    return or(
      exists(
        "group",
        (group) => group.whereExists("members", (q) => q.where("userId", "=", actor.userId)),
        { flip: true },
      ),
      exists(
        "grants",
        (r) =>
          AccessGrant.matching(
            r,
            actor,
            AccessGrant.criteria("space", "root", ["owner", "editor", "viewer"]),
          ),
        { flip: true },
      ),
    );
  }

  export const byMembership = defineQuery(z.undefined(), ({ ctx }) =>
    zql.space
      .where("timeArchived", "IS", null)
      .whereExists(
        "group",
        (q) => q.whereExists("members", (m) => m.where("userId", "=", ctx.actor.userId)),
        { flip: true },
      )
      .related("group", (g) => g.related("members", (m) => m.related("profile"))),
  );
  export type Preview = QueryRowType<typeof byMembership>;

  export const forManagement = defineQuery(z.undefined(), ({ ctx }) =>
    zql.space
      .where("timeArchived", "IS", null)
      .whereExists(
        "grants",
        (r) =>
          AccessGrant.matching(r, ctx.actor, AccessGrant.criteria("space", "contents", ["editor"])),
        { flip: true },
      )
      .related("group", (g) => g.related("members", (m) => m.related("profile"))),
  );

  export const byId = defineQuery(z.object({ id: z.uuid() }), ({ ctx, args: { id } }) =>
    zql.space
      .where("timeArchived", "IS", null)
      .where((e) => viewable(e, ctx.actor))
      .where("id", "=", id)
      .related("group", (g) => g.related("members", (m) => m.related("profile")))
      .one(),
  );
  export type Info = NonNullable<QueryRowType<typeof byId>>;

  export const memberGrants = defineQuery(
    z.object({ groupId: z.uuid() }),
    ({ args: { groupId } }) =>
      zql.accessGrant.where("resourceId", "=", groupId).where("resourceType", "=", "space"),
  );
  export type MemberGrant = QueryRowType<typeof memberGrants>;

  const canViewInvites = (
    { or, exists }: ExpressionBuilder<"groupInvite", Schema>,
    actor: UserActor,
  ) => {
    return or(
      exists(
        "grantsByGroup",
        (r) =>
          AccessGrant.matching(
            r,
            actor,
            AccessGrant.criteria("space", "root", ["owner", "editor", "viewer"]),
          ),
        { flip: true },
      ),
      exists(
        "grantsByGroup",
        (r) =>
          AccessGrant.matching(r, actor, AccessGrant.criteria("space", "contents", ["editor"])),
        { flip: true },
      ),
    );
  };

  export const pendingInvites = defineQuery(
    z.object({ groupId: z.uuid() }),
    ({ ctx, args: { groupId } }) => {
      const now = Temporal.Now.instant();
      return zql.groupInvite
        .whereExists(
          "group",
          (q) => q.whereExists("space", (p) => p.where("timeArchived", "IS", null)),
          { flip: true },
        )
        .where("groupId", "=", groupId)
        .where((e) => e.or(e.cmp("timeExpires", "IS", null), e.cmp("timeExpires", ">", now)))
        .where((e) => canViewInvites(e, ctx.actor));
    },
  );
  export type InvitePreview = QueryRowType<typeof pendingInvites>;

  export const create = withZeroTransaction(
    createInsertSchema(group).extend({
      name: z.string().min(1).max(255),
      members: createInsertSchema(groupMember).omit({ groupId: true }).array(),
    }),
    async ({ tx, ctx, args }) => {
      await Group.createBase(tx, { id: args.id });
      await createResources(tx, { id: args.id, name: args.name });
      await Promise.all(
        args.members.map(async (member) => {
          await Group.addMember(tx, { ...member, groupId: args.id });
        }),
      );
      await AccessGrant.add(tx, AccessGrant.assignment(args.id, "space", "root", "owner"), {
        type: "user",
        id: ctx.actor.userId,
      });
      await AccessGrant.add(tx, AccessGrant.assignment(args.id, "space", "contents", "editor"), {
        type: "user",
        id: ctx.actor.userId,
      });
    },
  );

  export const rename = withZeroTransaction(
    z.object({
      id: z.uuid(),
      name: z.string().min(1).max(255),
    }),
    async ({ tx, ctx, args: { id, name } }) => {
      await AccessGrant.require(
        tx,
        ctx.actor,
        Permissions.canRename,
        id,
        "You do not have permission to rename this group",
      );
      await requireActive(tx, id);
      await renameResources(tx, { id, name });
    },
  );

  export const archive = withZeroTransaction(
    z.object({ id: z.uuid() }),
    async ({ tx, ctx, args: { id } }) => {
      await lock(tx, id);
      await AccessGrant.require(
        tx,
        ctx.actor,
        Permissions.canDelete,
        id,
        "Only owners can delete a group",
      );
      const space = await tx.run(zql.space.where("id", "=", id).one());
      if (!space) {
        throw new Error("Space not found");
      }
      if (space.timeArchived) {
        return;
      }

      const timeNow = Temporal.Now.instant();
      await tx.mutate.space.update({ id, timeArchived: timeNow, timeUpdated: timeNow });
    },
  );

  export const leave = withZeroTransaction(
    z.object({ id: z.uuid() }),
    async ({ tx, ctx, args: { id } }) => {
      const userId = ctx.actor.userId;
      const memberExists = await Group.isMember(tx, userId, id);
      if (!memberExists) {
        accessDenied("You are not a member of this group");
      }
      const userOwnerGrant = await tx.run(
        zql.accessGrant
          .where("resourceId", "=", id)
          .where("resourceType", "=", "space")
          .where("resourceScope", "=", "root")
          .where("role", "=", "owner")
          .where("granteeType", "=", "user")
          .where("granteeId", "=", userId)
          .one(),
      );
      if (userOwnerGrant) {
        const ownerCount = await countOwners(tx, id);
        if (ownerCount <= 1) {
          accessDenied(
            "Cannot leave group: you are the last owner. Transfer ownership or delete the group.",
          );
        }
      }
      await requireActive(tx, id);
      await Group.removeMember(tx, { groupId: id, userId });
    },
  );

  export async function requireCanCreateInvite(
    tx: Transaction,
    actor: UserActor,
    groupId: string,
  ): Promise<void> {
    await AccessGrant.require(
      tx,
      actor,
      Permissions.canInvite,
      groupId,
      "You do not have permission to create invites for this group",
    );
    await requireActive(tx, groupId);
  }

  export async function requireCanRevokeInvite(
    tx: Transaction,
    actor: UserActor,
    args: { id: string; groupId: string },
  ): Promise<void> {
    await AccessGrant.require(
      tx,
      actor,
      Permissions.canRevokeInvite,
      args.groupId,
      "You do not have permission to revoke invites for this group",
    );
    await requireActive(tx, args.groupId);
    const invite = await Group.requireInviteById(tx, args.id);
    if (invite.groupId !== args.groupId) {
      throw new Error("Invite does not belong to this space");
    }
  }

  export async function acceptInvite(
    tx: Transaction,
    args: { token: string; userId: string; groupId: string },
  ): Promise<{ groupId: string }> {
    await lock(tx, args.groupId);
    await requireActive(tx, args.groupId);
    return Group.acceptInvite(tx, args);
  }

  export const removeMember = withZeroTransaction(
    z.object({ groupId: z.uuid(), userId: z.uuid() }),
    async ({ tx, ctx, args: { groupId, userId } }) => {
      if (userId === ctx.actor.userId) {
        accessDenied("Use leave() to remove yourself from a group");
      }
      await AccessGrant.require(
        tx,
        ctx.actor,
        Permissions.canRemoveMembers,
        groupId,
        "You do not have permission to remove members from this group",
      );
      const memberExists = await Group.isMember(tx, userId, groupId);
      if (!memberExists) {
        accessDenied("User is not a member of this group");
      }
      const targetIsOwner = await tx.run(
        zql.accessGrant
          .where("resourceId", "=", groupId)
          .where("resourceType", "=", "space")
          .where("resourceScope", "=", "root")
          .where("role", "=", "owner")
          .where("granteeType", "=", "user")
          .where("granteeId", "=", userId)
          .one(),
      );
      if (targetIsOwner) {
        accessDenied("Cannot remove an owner from the group");
      }
      await requireActive(tx, groupId);
      await Group.removeMember(tx, { groupId, userId });
      const userGrants = await tx.run(
        zql.accessGrant
          .where("resourceId", "=", groupId)
          .where("resourceType", "=", "space")
          .where("granteeType", "=", "user")
          .where("granteeId", "=", userId),
      );
      await Promise.all(userGrants.map((g) => tx.mutate.accessGrant.delete({ id: g.id })));
    },
  );

  export const setRootGrant = withZeroTransaction(
    z.object({
      groupId: z.uuid(),
      userId: z.uuid(),
      role: z.enum(["owner", "editor", "viewer"]).nullable(),
    }),
    async ({ tx, ctx, args: { groupId, userId, role } }) => {
      const memberExists = await Group.isMember(tx, userId, groupId);
      if (!memberExists) {
        accessDenied("User is not a member of this group");
      }

      const isSelf = userId === ctx.actor.userId;
      const [actorRootGrant, targetRootGrant] = await Promise.all([
        AccessGrant.forGrantee(tx, ctx.actor, groupId, "space", "root"),
        AccessGrant.forGrantee(tx, { type: "user", userId }, groupId, "space", "root"),
      ]);

      const actorRootRole = (actorRootGrant?.role as RootRole) ?? null;
      const targetRootRole = (targetRootGrant?.role as RootRole) ?? null;
      await requireActive(tx, groupId);

      if (isSelf) {
        const actorMembersGrant = await AccessGrant.forGrantee(
          tx,
          ctx.actor,
          groupId,
          "space",
          "contents",
        );
        const actorMembersRole = (actorMembersGrant?.role as ContentsRole) ?? null;
        const ownerCount = await countOwners(tx, groupId);
        const selfChanges = computeSelfRoleChanges(actorRootRole, actorMembersRole, ownerCount);

        if (!selfChanges.canModify) {
          accessDenied("Cannot demote the last owner");
        }
        if (!selfChanges.rootRoles.includes(role)) {
          accessDenied("You cannot promote yourself");
        }
      } else {
        const allowed = computeAllowedRoleChanges(actorRootRole, targetRootRole);
        if (!allowed.canModify) {
          accessDenied("You do not have permission to change root roles");
        }
        if (!allowed.rootRoles.includes(role)) {
          accessDenied("Only owners can grant owner or editor roles");
        }
      }

      if (targetRootGrant) {
        await tx.mutate.accessGrant.delete({ id: targetRootGrant.id });
      }
      if (role) {
        await AccessGrant.add(tx, AccessGrant.assignment(groupId, "space", "root", role), {
          type: "user",
          id: userId,
        });
      }
    },
  );

  export const setMembersGrant = withZeroTransaction(
    z.object({
      groupId: z.uuid(),
      userId: z.uuid(),
      role: z.enum(["editor", "viewer"]).nullable(),
    }),
    async ({ tx, ctx, args: { groupId, userId, role } }) => {
      const memberExists = await Group.isMember(tx, userId, groupId);
      if (!memberExists) {
        accessDenied("User is not a member of this group");
      }

      const isSelf = userId === ctx.actor.userId;
      const [actorRootGrant, targetMembersGrant] = await Promise.all([
        AccessGrant.forGrantee(tx, ctx.actor, groupId, "space", "root"),
        AccessGrant.forGrantee(tx, { type: "user", userId }, groupId, "space", "contents"),
      ]);

      const actorRootRole = (actorRootGrant?.role as RootRole) ?? null;
      const targetMembersRole = (targetMembersGrant?.role as ContentsRole) ?? null;
      await requireActive(tx, groupId);

      if (isSelf) {
        if (!targetMembersGrant) {
          accessDenied("You do not have a members grant to modify");
        }
        const actorMembersGrant = await AccessGrant.forGrantee(
          tx,
          ctx.actor,
          groupId,
          "space",
          "contents",
        );
        const actorMembersRole = (actorMembersGrant?.role as ContentsRole) ?? null;
        const ownerCount = await countOwners(tx, groupId);
        const selfChanges = computeSelfRoleChanges(actorRootRole, actorMembersRole, ownerCount);

        if (!selfChanges.contentsRoles.includes(role)) {
          accessDenied("You cannot promote yourself");
        }
      } else {
        const allowed = computeAllowedContentsRoleChanges(actorRootRole, targetMembersRole);
        if (!allowed.canModify) {
          accessDenied("You do not have permission to change member management roles");
        }
        if (!allowed.allowedRoles.includes(role)) {
          accessDenied("Only owners can grant members editor role");
        }
      }

      if (targetMembersGrant) {
        await tx.mutate.accessGrant.delete({ id: targetMembersGrant.id });
      }
      if (role) {
        await AccessGrant.add(tx, AccessGrant.assignment(groupId, "space", "contents", role), {
          type: "user",
          id: userId,
        });
      }
    },
  );
}
